import { useDrawerContext } from '@/components/layouts/DrawerProvider';
import { ConnectedAdvancedBuildingSelectorDrawerContent } from '@smartfm/container/components/ConnectedAdvancedBuildingSelectorDrawerContent';
import { useFavoritesContext } from '@smartfm/container/context/favorites';
import { useAdvancedNavigationTree } from '@smartfm/container/hooks/useAdvancedNavigationTree';
import { useBuildingIds } from '@smartfm/container/hooks/useBuildingIds';
import useFuse from '@smartfm/container/hooks/useFuse';
import { Button } from '@smartfm/ui/components/Button';
import { IconButton } from '@smartfm/ui/components/IconButton';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BuildingSelector,
  type BuildingSelectorProps,
} from './BuildingSelector';
import './styles.scss';

const searchDebounceMs = 1000;

interface ConnectedBuildingSelectorProps {
  position: NonNullable<BuildingSelectorProps['position']>;
  clientId: string;
  defaultBuilding?: string;
}

export function ConnectedBuildingSelector(
  props: ConnectedBuildingSelectorProps
) {
  const { clientId, ...otherProps } = props;
  const [, buildingIds, setBuildingIds] = useBuildingIds();
  const { setDrawerComponent } = useDrawerContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const {
    data: navigationTreeData,
    isLoading,
    isFetching,
  } = useAdvancedNavigationTree({
    clientId,
    enabled: Boolean(clientId),
  });

  const [searchField, setSearchField] = useState<string>();
  const debouncedSearchField = useDebounce(searchField, searchDebounceMs);
  const list = useMemo(
    () =>
      navigationTreeData?.itemsOrder.map(id => ({
        id,
        ...navigationTreeData?.itemsMap[id],
      })) || [],
    [navigationTreeData?.itemsOrder, navigationTreeData?.itemsMap]
  );
  const searchResults = useFuse(list, debouncedSearchField || '', {
    ignoreLocation: true,
    keys: ['label', 'secondaryText'],
    minMatchCharLength: 3,
    threshold: 0.3,
  });

  // Transform the data from useAdvancedNavigationTree to match the expected format
  const data = useMemo(() => {
    if (!navigationTreeData) return null;

    const { itemsMap, itemsOrder, totalItemsCount } = navigationTreeData;

    return {
      itemsMap,
      itemsOrder,
      totalCount: totalItemsCount,
    };
  }, [navigationTreeData]);

  // Keep the current label in case
  // the new search results don't contain the current value
  const defaultLabelRef = useRef('');

  useEffect(() => {
    if (!data) return;
    if (buildingIds.length === 0) return;

    const buildingId = buildingIds[0];
    const item = data.itemsMap[buildingId];

    // If the item is not found, do nothing
    if (!item) return;
    defaultLabelRef.current = item.label;
  }, [data, buildingIds]);

  const {
    favorites,
    toggleFavorite,
    isFavoriteFilterActive,
    toggleFavoriteFilter,
  } = useFavoritesContext();

  function handleValueChange(value: string) {
    setBuildingIds(
      value === BuildingSelector.__ALL_BUILDINGS_VALUE__ ? [] : [value]
    );
  }

  function handleOpen() {
    setIsMenuOpen(true);
  }

  // Reset the search field when the selector is closed
  function handleClose() {
    setSearchField('');
    setIsMenuOpen(false);
  }

  // Due to the way the EmeraldMenu component is implemented,
  // The key is required to force the component to re-render
  // To close the menu each time the value changes
  const [keyProp, setKeyProp] = useState('');

  function handleOnChange(ids: string[]) {
    setBuildingIds(ids);
  }

  function handleAdvancedSelectionClick() {
    if (!data) return;
    const values = buildingIds.length === 0 ? data.itemsOrder : buildingIds;
    setDrawerComponent(
      <ConnectedAdvancedBuildingSelectorDrawerContent
        clientId={clientId}
        values={values}
        /* eslint-disable-next-line react/jsx-no-bind */
        onChange={handleOnChange}
      />
    );
    setKeyProp(keyProp === '1' ? '2' : '1');
  }

  const handleValueChangeWrapper = (value: string) => {
    handleValueChange(value);
  };

  const handleSearchChange = (value: string) => {
    setSearchField(value);
  };

  const handleFavoriteChange = (id: string) => {
    toggleFavorite(id);
  };

  const handleFavoriteFilterChange = (isEnabled: boolean) => {
    toggleFavoriteFilter(isEnabled);
  };

  // Handle favorites dead-end state when switching between clients
  useEffect(() => {
    if (!searchField && favorites.length === 0) {
      toggleFavoriteFilter(false);
    }
  }, [searchField, favorites.length, toggleFavoriteFilter]);

  // Check once on mount if the favorites are in the current list
  useEffect(() => {
    if (!data) return;

    const { itemsOrder } = data;
    favorites.forEach(id => {
      // Do nothing if the item is in the current list
      if (itemsOrder.includes(id)) return;
      // Otherwise, remove it from the favorites
      toggleFavorite(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itemsOrder = useMemo(() => {
    if (searchResults.length > 0 || debouncedSearchField) {
      return searchResults.map(result => result.item.id);
    }

    return data?.itemsOrder || [];
  }, [searchResults, debouncedSearchField, data?.itemsOrder]);

  if (isLoading || (!isMenuOpen && isFetching)) {
    return (
      <Button
        size='Small'
        type='TextButton'
        className='building-selector__trigger'
        label='Finding Buildings ...'
        disabled={true}
      />
    );
  }

  if (!data) {
    return (
      <Button
        size='Small'
        type='TextButton'
        className='building-selector__trigger'
        label='No Buildings Found'
        disabled={true}
      />
    );
  }
  const { itemsMap, totalCount } = data;

  return (
    <BuildingSelector
      keyProp={keyProp}
      trailingHeaderElement={
        <IconButton
          icon='account_tree'
          iconFillType='rounded'
          size='Large'
          type='Light'
          title='Advanced building selection'
          onClick={handleAdvancedSelectionClick}
        />
      }
      // Value handling
      values={buildingIds}
      itemsMap={itemsMap}
      itemsOrder={itemsOrder}
      totalCount={totalCount}
      onValueChange={handleValueChangeWrapper}
      defaultLabel={defaultLabelRef.current}
      // Search handling
      searchValue={searchField}
      onSearchChange={handleSearchChange}
      onOpen={handleOpen}
      onClose={handleClose}
      // Favorite items handling
      favoriteItems={favorites}
      onFavoriteChange={handleFavoriteChange}
      isFavoriteFilterActive={isFavoriteFilterActive}
      onFavoriteFilterChange={handleFavoriteFilterChange}
      // Metadata
      isLoading={isFetching}
      {...otherProps}
    />
  );
}
